import { Currency, formatMoney, formatPercent, PaymentMethodModificationPreview } from '@nemuru-eng/js-api-client';
import { InsuranceProvider } from '@nemuru-eng/js-api-client/slim';

import { EditAutoPaymentMethodFinancingFields } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/schemas/editAutoPaymentMethodForm.schema';
import { createEditVehicleLiveSummaryFormatters } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/utils/editVehicleLiveSummaryFormatters';
import { GetTextIdTranslationType } from '@/components/shared/hooks/useTranslation';
import { amountToValueMoneyMapper } from '@/utils/mappers/amountToValueMoneyMapper';

export interface EditAutoPaymentMethodPreviewConditionsData {
  vehicleAmount: string;
  instalmentAmount: string;
  term: string;
  setupFee: string;
  annualInterest: string;
  apr: string;
  insurance: string;
  downPayment: string;
  discount: string;
  totalToFinance: string;
  insuranceProvider: InsuranceProvider | null;
}

interface CreateEditAutoPaymentMethodPreviewConditionsParams {
  preview: PaymentMethodModificationPreview | undefined;
  financing: EditAutoPaymentMethodFinancingFields;
  currency: Currency;
  fallbackApr: number | null | undefined;
  getTextIdTranslation: GetTextIdTranslationType;
}

export const createEditAutoPaymentMethodPreviewConditions = ({
  preview,
  financing,
  currency,
  fallbackApr,
  getTextIdTranslation,
}: CreateEditAutoPaymentMethodPreviewConditionsParams): EditAutoPaymentMethodPreviewConditionsData => {
  const emptyValue = '-';
  const { formatMoneyAmount, formatTerm } = createEditVehicleLiveSummaryFormatters();

  const instalmentAmount =
    preview?.newQuota != null
      ? formatMoney(amountToValueMoneyMapper(preview.newQuota), 2)
      : formatMoneyAmount(financing.instalmentAmount, currency);

  const term = preview?.newTerm != null ? String(preview.newTerm) : formatTerm(financing.term);

  const setupFee = formatMoneyAmount(financing.setupFeeAmount, currency);

  const annualInterest =
    financing.annualInterest === null || financing.annualInterest === undefined
      ? emptyValue
      : formatPercent(financing.annualInterest);

  const apr =
    preview?.newApr != null
      ? formatPercent(preview.newApr)
      : fallbackApr != null
      ? formatPercent(fallbackApr)
      : emptyValue;

  const insurance = formatInsurance(financing.insurance, getTextIdTranslation, emptyValue);
  const downPayment =
    financing.downPaymentAmount && financing.downPaymentAmount > 0
      ? formatMoneyAmount(financing.downPaymentAmount, currency)
      : formatMoneyAmount(0, currency);
  const discount =
    financing.discountAmount && financing.discountAmount > 0
      ? formatMoneyAmount(financing.discountAmount, currency)
      : formatMoneyAmount(0, currency);
  const totalToFinance = formatMoneyAmount(financing.principalAmount, currency);

  const vehicleAmount = formatMoneyAmount(
    (financing.principalAmount ?? 0) + (financing.downPaymentAmount ?? 0) + (financing.discountAmount ?? 0),
    currency,
  );

  return {
    vehicleAmount,
    instalmentAmount,
    term,
    setupFee,
    annualInterest,
    apr,
    insurance,
    downPayment,
    discount,
    totalToFinance,
    insuranceProvider: financing.insurance ? financing.insuranceProvider : null,
  };
};

const formatInsurance = (
  insuranceType: string | null | undefined,
  getTextIdTranslation: GetTextIdTranslationType,
  emptyValue: string,
): string => {
  if (!insuranceType) {
    return emptyValue;
  }

  return getTextIdTranslation(`INSURANCE_TYPE_${insuranceType}`) || insuranceType;
};
